/* cf-rake-align.js - Click Fencing, 2026-09-02
 *
 * Pete: "the rake panels still look awful. on every drawing the bottom of the rake panel needs
 *        to line up with the bottom of the other glass panels. the change of angle in the rake
 *        panel has to line up with the top of the glass panels next to it. also it cant
 *        flicker like it does. fix this for good."
 *
 * =========================================================================================
 * WHAT A RAKED GLASS PANEL ACTUALLY IS, measured on the live calculator - 8690mm section,
 * 1600mm panels, 1800mm retained height, at 1400x900:
 *
 *     plain 1450mm panels   glass div 181 x 163 px, top 755, bottom 917
 *     raked 1200mm panel    inline <svg> width=150 height=225 viewBox="0 0 150 225"
 *                           polygon "0,0 56.3,0 150,75 150,225 0,225"
 *
 * The polygon is a rectangle with the top-right corner cut away. Read off it:
 *
 *     bottom edge          y = 225
 *     the SHOULDER, where the angle changes, on the short side   y = 75
 *     so the straight part of the panel is 225 - 75 = 150 units
 *
 * And the units are honest: 225 units = the 1800mm retained height and 150 units = the
 * 1200mm panel, both at 0.125 units/mm, which is also exactly the row's horizontal scale
 * (181.25px / 1450mm = 0.125). The artwork was never wrong. Only its rendered size was.
 *
 * WHY IT LOOKED AWFUL. The stylesheet caps `#section-preview svg` at max-height:130px. With
 * a 150x225 viewBox that forces preserveAspectRatio to scale the whole drawing to 0.578 and
 * centre it in a 150x130 box - so the rake was drawn at a different scale from the plain
 * <div> panels standing beside it, and lining it up was never possible.
 *
 * THE RULE, which is Pete's two sentences turned into arithmetic:
 *
 *     sY = (neighbouring glass height) / (bottomY - shoulderY)
 *     rendered height = viewBox height * sY,  bottom on the row's ground line
 *
 * Do that and BOTH his conditions hold by construction rather than by tuning: the bottom is
 * on the ground line because we put it there, and the shoulder lands on the neighbours' top
 * because the straight part of the panel is now exactly as tall as they are. Checked against
 * the numbers above: sY = 163/150 = 1.087, height = 225 * 1.087 = 244.5, top = 917 - 244.5 =
 * 672.5, shoulder = 672.5 + 75 * 1.087 = 754 against a neighbour top of 755.
 *
 * WIDTH IS SCALED SEPARATELY, and that is deliberate. The preview is not isotropic - the row
 * is 0.125 px/mm across and 0.136 px/mm up - so forcing one scale on both axes would either
 * make the rake the wrong width or the wrong height. preserveAspectRatio="none" lets the
 * width follow the row's horizontal scale (the container the calculator already sized) while
 * the height follows the rule above. The neighbouring panels are non-uniformly scaled divs
 * too, so this matches them rather than fighting them.
 *
 * =========================================================================================
 * AND THE FLICKER, which was three files writing one element.
 *
 *     cf-preview-scroll.js      height:150px !important  (128 on mobile), every 1200ms
 *     cf-preview-legible.js     height:140px !important  (120 on mobile), on a rAF
 *     cf-glass-preview-fixes.js the intrinsic 150x225 !important, on its own schedule
 *
 * All three matched the per-panel artwork through selectors written for the ONE fence-wide
 * drawing that blade, flat top and mesh render - "#section-preview svg" and
 * ".panels-container svg" catch both. Each has its own timer, so they took turns and the
 * panel visibly changed size. It is the same defect as the quote-review button that flickered
 * between "Continue to Payment" and "Book Free Site Inspection": more than one owner.
 *
 * So this file is made the SINGLE owner of the rake artwork's size:
 *   - cf-preview-legible.js and cf-preview-scroll.js now skip any svg inside a .panel-preview
 *     (the fence-wide drawing they exist for never has that ancestor);
 *   - and the width/height ATTRIBUTES are moved to data-cf-rake-w / data-cf-rake-h, which
 *     makes cf-glass-preview-fixes.js's `if (w && h)` size-forcing block a no-op without
 *     that file needing to change. Its BOTTOM seating still runs, and is still wanted - it
 *     is what puts the panel on the ground line, and it already worked.
 *
 * Nothing here is on a timer of its own beyond a slow safety net, and every write is guarded
 * on a real change, so once the row settles this file stops touching the DOM entirely - which
 * is what makes the flicker impossible rather than merely slower.
 *
 * Display only: no price, product, quantity or cart line is touched.
 */
(function () {
  'use strict';

  if (window.__cfRakeAlign) return;
  window.__cfRakeAlign = true;

  var LOG = '[cf-rake-align]';
  var W0 = 'data-cf-rake-w', H0 = 'data-cf-rake-h';
  var DONE = 'data-cf-rake-done';

  function isRakePanel(p) {
    var l = p.querySelector('.panel-width');
    return !!(l && /rake/i.test(l.textContent || ''));
  }

  /* THE GLASS, NOT THE WRAPPER AROUND IT AND ITS SPIGOTS. Corrected 2026-09-02 after Pete:
     "the base of the glass panels isnt aligned still. move the bottom of the glass rake panel
     up to line up with the bottom of the glass panel next to it. not the bottom of the
     spigots."

     He was right, and the DOM says why. A plain panel is three levels, measured live:

         .panel-preview          top 626  bottom 823
           wrapper               top 626  bottom 789   height 163
             GLASS               top 626  bottom 776   height 150
             spigot divs         top 771  bottom 791   (narrow, w 7 and 11)

     The first child with real height is the WRAPPER, and its bottom (789) is the spigot line,
     not the glass line (776). Seating the rake on 789 put its glass 13px below its neighbours'
     - exactly what Pete photographed - and made the panel 163px of straight instead of 150.

     So descend one more level and take the full-width child. The spigots are siblings of the
     glass and only 7-11px wide, so "widest child of the wrapper" picks the glass every time
     without hard-coding a class the theme could rename. If nothing full-width is found the
     wrapper is returned, which is the old behaviour rather than no alignment at all. */
  function glassBox(p) {
    var k = p.children, wrap = null;
    for (var i = 0; i < k.length; i++) {
      if (k[i].classList && k[i].classList.contains('panel-width')) continue;
      var r = k[i].getBoundingClientRect();
      if (r.height > 4) { wrap = k[i]; break; }
    }
    if (!wrap) return null;
    var wr = wrap.getBoundingClientRect();
    var best = null, bestW = 0;
    for (var j = 0; j < wrap.children.length; j++) {
      var cr = wrap.children[j].getBoundingClientRect();
      if (cr.height > 4 && cr.width > bestW) { bestW = cr.width; best = cr; }
    }
    return (best && bestW >= wr.width * 0.8) ? best : wr;
  }

  /* A row is the nearest ancestor holding more than one panel. Every .panel-preview sits in
     its own wrapper div, so grouping by parentNode would put each panel in a row of one and
     the rake would have nothing to measure against - a trap that already cost a session. */
  function rowAncestor(p) {
    var el = p.parentNode, hops = 0;
    while (el && hops < 8) {
      if (el.querySelectorAll && el.querySelectorAll('.panel-preview').length > 1) return el;
      el = el.parentNode; hops++;
    }
    return null;
  }

  function rows() {
    var all = [].slice.call(document.querySelectorAll('.panel-preview'));
    var out = [];
    for (var i = 0; i < all.length; i++) {
      var key = rowAncestor(all[i]);
      if (!key) continue;
      var idx = -1;
      for (var j = 0; j < out.length; j++) if (out[j].parent === key) { idx = j; break; }
      if (idx < 0) { out.push({ parent: key, items: [] }); idx = out.length - 1; }
      out[idx].items.push(all[i]);
    }
    return splitByLine(out);
  }

  /* 2026-10-09: on a phone a long glass fence wraps into ROWS inside one container (cf-preview-phone.js),
     so one container can hold two or three visual lines, each with its own ground. Seating a rake to the
     container's most common ground pushed it down onto the next line (Pete: Review page test, rake + two
     gates). Split each group into lines: slots on one line share a bottom edge (the row is
     align-items:flex-end), lines are far apart. On one line (desktop, short fences) nothing changes. */
  function splitByLine(groups) {
    var out = [];
    groups.forEach(function (g) {
      var lines = [];
      g.items.forEach(function (p) {
        var slot = p;
        while (slot.parentNode && slot.parentNode !== g.parent) slot = slot.parentNode;
        var b = slot.getBoundingClientRect ? slot.getBoundingClientRect().bottom : 0;
        var hit = null;
        for (var k = 0; k < lines.length; k++) if (Math.abs(lines[k].b - b) < 20) { hit = lines[k]; break; }
        if (!hit) { hit = { b: b, items: [] }; lines.push(hit); }
        hit.items.push(p);
      });
      if (lines.length < 2) { out.push(g); return; }
      lines.forEach(function (l) { out.push({ parent: g.parent, items: l.items }); });
    });
    return out;
  }


  /* Mode rather than mean, so one already-misplaced panel cannot drag the whole row. */
  function modeOf(values) {
    var tally = {}, best = null, bestN = 0;
    for (var i = 0; i < values.length; i++) {
      var k = Math.round(values[i]);
      tally[k] = (tally[k] || 0) + 1;
      if (tally[k] > bestN) { bestN = tally[k]; best = k; }
    }
    return best;
  }

  function plainMetrics(row) {
    var heights = [], bottoms = [];
    for (var i = 0; i < row.length; i++) {
      if (isRakePanel(row[i])) continue;
      var b = glassBox(row[i]);
      if (!b) continue;
      heights.push(b.height);
      bottoms.push(b.bottom);
    }
    if (!heights.length) return null;
    return { height: modeOf(heights), ground: modeOf(bottoms) };
  }

  /* ------------------------------------------------- reading the shape */

  function pointsOf(poly) {
    var raw = poly.getAttribute('points') || '';
    var nums = raw.trim().split(/[\s,]+/).map(Number);
    var pts = [];
    for (var i = 0; i + 1 < nums.length; i += 2) {
      if (isFinite(nums[i]) && isFinite(nums[i + 1])) pts.push([nums[i], nums[i + 1]]);
    }
    return pts;
  }

  /* The outline, not the highlight. The artwork holds several polygons - the glass fill, a
     narrow reflection streak, the stroked outline, and a copy inside a clipPath - so the one
     that describes the panel is simply the one covering the most area. */
  function outlinePoints(svg) {
    var polys = svg.querySelectorAll('polygon');
    var best = null, bestArea = 0;
    for (var i = 0; i < polys.length; i++) {
      var pts = pointsOf(polys[i]);
      if (pts.length < 4) continue;
      var xs = pts.map(function (p) { return p[0]; });
      var ys = pts.map(function (p) { return p[1]; });
      var area = (Math.max.apply(null, xs) - Math.min.apply(null, xs)) *
                 (Math.max.apply(null, ys) - Math.min.apply(null, ys));
      if (area > bestArea) { bestArea = area; best = pts; }
    }
    return best;
  }

  /* THE SHOULDER is the top of the SHORT vertical side - the side that stands alongside a
     normal panel. One side runs the full height of the artwork (the retained end) and the
     other stops where the slope meets it. Taking the LOWER of the two side-tops finds the
     short side whichever way round the rake is drawn, so a rake sloping left and a rake
     sloping right both work without a special case. */
  function shape(svg) {
    var pts = outlinePoints(svg);
    if (!pts || pts.length < 4) return null;
    var xs = pts.map(function (p) { return p[0]; });
    var ys = pts.map(function (p) { return p[1]; });
    var minX = Math.min.apply(null, xs), maxX = Math.max.apply(null, xs);
    var bottomY = Math.max.apply(null, ys);

    function topAt(x) {
      var t = null;
      for (var i = 0; i < pts.length; i++) {
        if (Math.abs(pts[i][0] - x) > 0.51) continue;
        if (t === null || pts[i][1] < t) t = pts[i][1];
      }
      return t;
    }
    var left = topAt(minX), right = topAt(maxX);
    if (left === null || right === null) return null;
    var shoulderY = Math.max(left, right);
    if (bottomY - shoulderY < 1) return null;      /* not a rake - nothing to align */
    return { shoulderY: shoulderY, bottomY: bottomY };
  }

  function viewBoxOf(svg) {
    var raw = svg.getAttribute('viewBox');
    if (!raw) return null;
    var v = raw.trim().split(/[\s,]+/).map(Number);
    if (v.length !== 4 || !v[2] || !v[3]) return null;
    return { x: v[0], y: v[1], w: v[2], h: v[3] };
  }

  /* ------------------------------------------------- the sizing */

  function setPx(el, prop, px) {
    var want = px.toFixed(1) + 'px';
    if (el.style.getPropertyValue(prop) === want) return false;
    el.style.setProperty(prop, want, 'important');
    return true;
  }

  function alignOne(panel, metrics) {
    var svg = panel.querySelector('svg');
    if (!svg) return false;

    /* Move the intrinsic size out of the ATTRIBUTES. cf-glass-preview-fixes.js forces the
       artwork back to those two numbers whenever it finds them, which is half of the flicker;
       with them gone its size-forcing block is skipped and only its bottom seating runs,
       which is the part worth keeping. The values are stashed, not lost. */
    var aw = svg.getAttribute('width'), ah = svg.getAttribute('height');
    if (aw && ah) {
      if (!svg.getAttribute(W0)) svg.setAttribute(W0, aw);
      if (!svg.getAttribute(H0)) svg.setAttribute(H0, ah);
      svg.removeAttribute('width');
      svg.removeAttribute('height');
    }

    var vb = viewBoxOf(svg);
    var sh = shape(svg);
    if (!vb || !sh) return false;

    /* Height from Pete's rule: the straight part of the rake must be exactly as tall as the
       panels beside it, which puts the shoulder on their top edge. */
    var sY = metrics.height / (sh.bottomY - sh.shoulderY);

    /* Width from the row's own horizontal scale. The container is the width the calculator
       already worked out for this panel, so matching it keeps the rake the right width
       relative to its neighbours - which is a different scale from the vertical one, hence
       preserveAspectRatio="none" below. */
    var boxW = panel.getBoundingClientRect().width ||
               parseFloat(svg.getAttribute(W0)) || vb.w;
    var sX = boxW / vb.w;

    var changed = false;
    if (svg.getAttribute('preserveAspectRatio') !== 'none') {
      svg.setAttribute('preserveAspectRatio', 'none');
      changed = true;
    }
    changed = setPx(svg, 'width', vb.w * sX) || changed;
    changed = setPx(svg, 'height', vb.h * sY) || changed;

    /* The stylesheet caps this artwork at max-height:130px and width:100%; both have to go or
       the rule above cannot take effect. */
    var fixed = { 'max-width': 'none', 'max-height': 'none', 'flex': 'none', 'display': 'block' };
    for (var k in fixed) {
      if (svg.style.getPropertyValue(k) !== fixed[k]) {
        svg.style.setProperty(k, fixed[k], 'important');
        changed = true;
      }
    }

    /* Anchor the BOTTOM rather than the top, so a change of height grows the panel upwards
       into the rake instead of pushing it through the ground line. */
    if (svg.style.getPropertyValue('top') !== 'auto') {
      svg.style.setProperty('top', 'auto', 'important');
      changed = true;
    }
    var cur = svg.getBoundingClientRect().bottom;
    if (cur) {
      var prev = parseFloat(svg.style.bottom) || 0;
      var delta = metrics.ground - cur;
      if (Math.abs(delta) > 0.5) {
        svg.style.setProperty('bottom', (prev - delta).toFixed(1) + 'px', 'important');
        changed = true;
      }
    }
    /* Stamped so a REDRAWN panel can be told from a settled one - see the observer in boot().
       A redraw replaces the <svg> node, so the new one arrives without this. */
    if (!svg.getAttribute(DONE)) svg.setAttribute(DONE, '1');
    return changed;
  }

  /* ------------------------------------------------- the field label

     Pete, 2026-09-02: "change this item to be called Retained Panel Height (mm)".

     The control is a <label> with no `for` attribute sitting beside the select, in a template
     large enough that re-uploading it to change three words is a poor trade - so it is
     relabelled here, next to the rest of the retained-height work. Matched from the SELECT
     outwards rather than by scanning the page for the words, so nothing else that happens to
     say "Retained Height" can be caught, and guarded on a real change so a settled page does
     no work. Every calculator that has a rake uses the same id pattern, so this covers all of
     them. */
  var WANT_LABEL = 'Retained Panel Height (mm)';

  function relabelRetained() {
    try {
      var sels = document.querySelectorAll('[id$="-retained-height"]');
      for (var i = 0; i < sels.length; i++) {
        var e = sels[i].parentElement, hops = 0, done = false;
        while (e && hops < 4 && !done) {
          var labs = e.querySelectorAll('label');
          for (var j = 0; j < labs.length; j++) {
            var l = labs[j];
            if (l.children.length) continue;
            var t = (l.textContent || '').trim();
            if (!/^Retained\s+Height/i.test(t)) continue;
            if (t !== WANT_LABEL) l.textContent = WANT_LABEL;
            done = true;
            break;
          }
          e = e.parentElement; hops++;
        }
      }
    } catch (e) {}
  }

  function alignAll() {
    relabelRetained();
    var touched = false;
    var rs = rows();
    for (var r = 0; r < rs.length; r++) {
      var row = rs[r].items;
      var metrics = plainMetrics(row);
      if (!metrics || !metrics.height) continue;    /* a row of only rakes - nothing to match */
      for (var i = 0; i < row.length; i++) {
        if (!isRakePanel(row[i])) continue;
        try { if (alignOne(row[i], metrics)) touched = true; } catch (e) {}
      }
    }
    return touched;
  }

  /* Two passes: the first sets the size, the second seats the bottom now that the size is
     known. It stops as soon as a pass changes nothing, so a settled row costs one pass. */
  function run() {
    try {
      for (var pass = 0; pass < 3; pass++) if (!alignAll()) break;
    } catch (e) {}
  }

  /* =======================================================================================
     A MISSING RETAINED HEIGHT WAS BEING REPORTED AS A SECTION THAT IS TOO SHORT.

     Pete, 2026-09-02: "just got a massive validation error for glass!!! completely incorrect,
     the section is massive", then "that validation error seemed to stem from me not picking
     the retained height of the rake".

     He was right, and it is provable. On a 9850mm section with a gate and a rake:

         retained height dropdown EMPTY  ->  validateSection(1) === false
         same section, dropdown = 1300   ->  validateSection(1) === true

     and the popup he saw read "Section 1 is incomplete. This section is too short to fit the
     gate and raked panel. Increase the section length or remove the gate." Nothing about the
     section was too short - the solver had already laid it out perfectly:
     1200 rake + 1500 hinge + 800 gate + [1600,1550,1550,1550] + 100mm of gaps = 9850mm
     exactly, remaining 0, withinVarianceLimit true. The advice was actively harmful: it sent
     him off to lengthen a fence that fitted, or delete a gate he wanted.

     WHY THE EXISTING GUARD DID NOT CATCH IT. cf-glass-preview-fixes.js already wraps
     validateSection to demand a retained height, but it only speaks up when the ORIGINAL
     validation passed:

         if (ok !== false && retainedMissing(this, n)) { ...our clear message... }

     With the dropdown empty the original ALSO fails, so `ok` is false, our branch is skipped
     and the calculator's own misleading message is what the customer reads. The check was in
     the right place and the wrong order.

     THE FIX. Ask about the retained height FIRST, before the original validation runs at all,
     so the specific fault is named and the generic message never gets the chance. Done from
     out here as an outer wrapper rather than by editing that 20KB file, which keeps the change
     small and separately reversible. A genuinely too-short section still gets the original
     message - this only pre-empts it for the one fault it misdiagnoses. */

  /* Pete's own words for it: "it should say please select the retained height of you rake
     panel". Lead with the instruction; the reason follows for anyone who wants it. */
  function rakeMsg(n) {
    return 'Please select the Retained Height for your raked panel in section ' + n + '. ' +
           'The raked panel is cut to suit your slope, so it cannot be sized or priced ' +
           'until you choose one.';
  }

  function retainedMissing(inst, n) {
    try {
      var s = inst && inst.state && inst.state.sections && inst.state.sections[n];
      if (!s || !s.hasRaked) return false;
      var sel = document.getElementById('section-' + n + '-retained-height');
      /* The DROPDOWN is the truth, not the state: its options start at 1300mm, so a value
         written into state that is not one of them leaves the control empty and the solver
         without a height. */
      var chosen = sel ? String(sel.value || '').trim()
                       : String(s.retainedHeight || '').trim();
      return !chosen;
    } catch (e) { return false; }
  }

  function flagRetained(n) {
    try {
      var sel = document.getElementById('section-' + n + '-retained-height');
      if (!sel) return;
      sel.style.setProperty('border', '2px solid #c0392b', 'important');
      sel.style.setProperty('background', '#fdf3f2', 'important');
      try { sel.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
      try { sel.focus(); } catch (e) {}
      if (!sel.__cfRakeClear) {
        sel.__cfRakeClear = true;
        sel.addEventListener('change', function () {
          sel.style.removeProperty('border');
          sel.style.removeProperty('background');
        });
      }
    } catch (e) {}
  }

  function wrapValidate() {
    var Ctor = window.GlassFencingCalculator ||
               (window.glassCalculator && window.glassCalculator.constructor);
    if (!Ctor || !Ctor.prototype) return false;
    var P = Ctor.prototype;
    if (P.__cfRakeValidate) return true;
    if (typeof P.validateSection !== 'function') return false;

    /* The gate: stop the customer advancing, and mark the field. It deliberately does NOT
       raise a popup - the caller goes on to call showSectionValidationError(), which is what
       actually speaks, and two popups for one fault is worse than the wrong one. */
    var orig = P.validateSection;
    P.validateSection = function (n) {
      try {
        if (retainedMissing(this, n)) {
          flagRetained(n);
          return false;
        }
      } catch (e) {}
      return orig.apply(this, arguments);
    };

    /* THE MESSAGE. This is the method that was lying, and the reason the first attempt at
       this fix did nothing: the popup never came from validateSection at all. Read out of
       ullc-calc.js:

           showSectionValidationError(sectionNum){
             ...
             (section.hasGate||section.hasRaked) && (message +=
               `This section is too short to fit the ${...}. Increase the section length or
                remove the ${...}.`),
             this.showValidationPopup(message, ...)

       It composes its text from hasGate/hasRaked and NEVER looks at the retained height, so a
       missing dropdown came out as a section that is too short - on Pete's 9850mm run that
       laid out to the millimetre. Wrapping it here names the real fault and leaves every other
       validation message exactly as it was. */
    if (typeof P.showSectionValidationError === 'function') {
      var origErr = P.showSectionValidationError;
      P.showSectionValidationError = function (n) {
        try {
          if (retainedMissing(this, n)) {
            flagRetained(n);
            if (typeof this.showValidationPopup === 'function') {
              return this.showValidationPopup(rakeMsg(n));
            }
            try { window.alert(rakeMsg(n)); } catch (e2) {}
            return;
          }
        } catch (e) {}
        return origErr.apply(this, arguments);
      };
    }

    P.__cfRakeValidate = true;
    try { console.log(LOG, 'retained-height message installed'); } catch (e) {}
    return true;
  }

  /* Wrap only once cf-glass-preview-fixes.js has installed its own wrapper, so ours sits
     OUTSIDE it and is asked first. The fallback means a build without that file still gets
     the clear message rather than nothing. */
  (function waitForPreviewFixes() {
    var tries = 0;
    (function poll() {
      var Ctor = window.GlassFencingCalculator ||
                 (window.glassCalculator && window.glassCalculator.constructor);
      var ready = Ctor && Ctor.prototype && Ctor.prototype.__cfGlassPreviewFixes;
      if (ready || ++tries > 120) { try { wrapValidate(); } catch (e) {} return; }
      setTimeout(poll, 100);
    })();
  })();

  /* ------------------------------------------------- boot */

  var timer = null;
  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(run, 120);
  }

  /* THE REDRAW JUMP. Pete: "when i first put it in it is to low then moves to the right
     position. then i add a gate and it lowers then goes into the right position?"

     Half of that was cf-glass-preview-fixes.js seating to the spigot line - it now stands
     down. The other half is latency: the calculator draws the rake at its own default and
     this file corrected it 120ms later, so every redraw painted the wrong position first.
     Making the debounce shorter would only shorten the jump, not remove it.

     A MutationObserver callback runs as a microtask at the END of the current task and
     BEFORE the next paint, so correcting there means the first frame the customer ever sees
     is already right. That is done ONLY for a freshly created artwork - one without the DONE
     stamp - because it forces a synchronous layout and must not run on every stray mutation.
     Everything else still goes through the debounce.

     The observer is disconnected around the run and its records drained before reconnecting,
     so our own writes cannot feed it. */
  function runSyncIfNew(obs) {
    var fresh = null;
    try { fresh = document.querySelector('.panel-preview svg:not([' + DONE + '])'); } catch (e) { return false; }
    if (!fresh) return false;
    try { if (obs) obs.disconnect(); } catch (e) {}
    try { run(); } catch (e) {}
    try {
      if (obs) {
        obs.takeRecords();
        obs.observe(observerRoot(), { childList: true, subtree: true });
      }
    } catch (e) {}
    return true;
  }

  function observerRoot() {
    return document.querySelector('.calculator-content') ||
           document.getElementById('qr-section-diagrams') ||
           document.getElementById('section-preview') || document.body;
  }

  function boot() {
    run();
    try {
      var root = observerRoot();
      if (window.MutationObserver) {
        var obs = new MutationObserver(function () {
          if (!runSyncIfNew(obs)) schedule();
        });
        obs.observe(root, { childList: true, subtree: true });
      }
      window.addEventListener('resize', schedule);
    } catch (e) {}
    /* A slow safety net only - the calculator redraws in ways an observer on one root can
       miss. Every write is guarded on a real change, so a settled page does nothing here. */
    setInterval(run, 2000);
    try { console.log(LOG, 'installed'); } catch (e) {}
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
